const NAV_SELECTOR = 'body.single-product .product-details-tabs';
const PLACEHOLDER_CLASS = 'product-details-tabs-placeholder';

function readCssPx(name, fallback = 0, el = document.documentElement) {
  const value = getComputedStyle(el).getPropertyValue(name).trim();

  return value ? parseFloat(value) : fallback;
}

function headerStackPx() {
  return readCssPx('--wp-admin--admin-bar--height') + readCssPx('--header-height', 100, document.body);
}

function initProductSectionTabs(nav) {
  if (nav.dataset.tabsBound) {
    return;
  }

  nav.dataset.tabsBound = 'true';

  const links = [...nav.querySelectorAll('.product-details-tabs__link')];

  if (!links.length) {
    return;
  }

  let placeholder = nav.previousElementSibling;

  if (!placeholder?.classList.contains(PLACEHOLDER_CLASS)) {
    placeholder = document.createElement('div');
    placeholder.className = PLACEHOLDER_CLASS;
    placeholder.setAttribute('aria-hidden', 'true');
    nav.before(placeholder);
  }

  const sections = links
    .map((link) => document.getElementById(link.dataset.anchor))
    .filter(Boolean);

  const visibleSections = new Map();
  let observer;
  let rafId = 0;

  const setActive = (activeLink) => {
    links.forEach((link) => {
      link.classList.toggle('is-active', link === activeLink);
    });
  };

  const syncLayout = () => {
    rafId = 0;

    const height = nav.getBoundingClientRect().height;

    if (height > 0) {
      document.body.style.setProperty('--product-tabs-height', `${Math.ceil(height)}px`);
    }

    const stack = headerStackPx();
    const fixed = placeholder.getBoundingClientRect().top <= stack;

    nav.classList.toggle('is-fixed', fixed);
    placeholder.style.height = fixed ? `${nav.offsetHeight}px` : '0';
  };

  const scheduleLayout = () => {
    if (!rafId) {
      rafId = requestAnimationFrame(syncLayout);
    }
  };

  const createObserver = () => {
    if (!('IntersectionObserver' in window) || !sections.length) {
      return;
    }

    observer?.disconnect();

    const offset = headerStackPx() + (nav.offsetHeight || readCssPx('--product-tabs-height', 65, document.body));

    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(({ target, intersectionRatio }) => {
          visibleSections.set(target.id, intersectionRatio);
        });

        let activeId = '';
        let bestRatio = 0;

        visibleSections.forEach((ratio, id) => {
          if (ratio > bestRatio) {
            bestRatio = ratio;
            activeId = id;
          }
        });

        const link = links.find((item) => item.dataset.anchor === activeId);

        if (link) {
          setActive(link);
        }
      },
      {
        rootMargin: `-${offset}px 0px -55% 0px`,
        threshold: [0, 0.1, 0.25, 0.5, 0.75, 1],
      },
    );

    sections.forEach((section) => observer.observe(section));
  };

  const onLayoutChange = () => {
    scheduleLayout();
    createObserver();
  };

  links.forEach((link) => {
    link.addEventListener('click', (event) => {
      const section = document.getElementById(link.dataset.anchor);

      if (!section) {
        return;
      }

      event.preventDefault();
      setActive(link);
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      history.replaceState?.(null, '', `#${link.dataset.anchor}`);
    });
  });

  const syncFromHash = () => {
    const link = links.find((item) => item.dataset.anchor === location.hash.slice(1));

    if (link) {
      setActive(link);
    }
  };

  syncFromHash();
  addEventListener('hashchange', syncFromHash);

  syncLayout();
  createObserver();

  addEventListener('scroll', scheduleLayout, { passive: true });
  addEventListener('resize', onLayoutChange);

  if (typeof ResizeObserver !== 'undefined') {
    new ResizeObserver(onLayoutChange).observe(nav);
  }
}

function initAllProductSectionTabs() {
  document.querySelectorAll(NAV_SELECTOR).forEach(initProductSectionTabs);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAllProductSectionTabs);
} else {
  initAllProductSectionTabs();
}
